/* Dedicated kiosk translations are bundled locally. */
/* eslint-disable i18next-no-undefined-translation-keys/no-undefined-translation-keys */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import query from "@/Utils/request/query";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Search, Stethoscope, X } from "lucide-react";
import { useEffect, useState } from "react";
import { kioskApi, KioskOption } from "./api";
import i18n from "./kioskTranslations";

export default function DoctorSelection({
  checkInKey,
  token,
  day,
  language,
  busy,
  onSelect,
}: {
  checkInKey: string;
  token: string;
  day?: string;
  language: string;
  busy: boolean;
  onSelect: (doctor: KioskOption) => void;
}) {
  const t = i18n.getFixedT(language, "kiosk");
  const [tab, setTab] = useState("doctors");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [offset, setOffset] = useState(0);
  const [department, setDepartment] = useState<KioskOption>();
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const { data, isFetching, error } = useQuery({
    queryKey: [
      "kiosk-doctors",
      checkInKey,
      token,
      tab,
      day,
      debounced,
      department?.id,
      offset,
    ],
    queryFn: query(kioskApi.list, {
      pathParams: { key: checkInKey },
      headers: { Authorization: `Bearer ${token}` },
      queryParams: {
        kind: tab,
        day,
        search: debounced,
        department: tab === "doctors" ? department?.id : undefined,
        offset,
      },
      silent: true,
    }),
    gcTime: 0,
    retry: false,
  });
  const changeTab = (value: string) => {
    setTab(value);
    setSearch("");
    setDebounced("");
    setOffset(0);
  };
  return (
    <div className="space-y-3">
      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="grid grid-cols-2 w-full h-auto p-1">
          <TabsTrigger
            className="min-h-12 text-base whitespace-normal"
            value="doctors"
          >
            <Stethoscope className="size-5" />
            {t("allDoctors")}
          </TabsTrigger>
          <TabsTrigger
            className="min-h-12 text-base whitespace-normal"
            value="departments"
          >
            <Building2 className="size-5" />
            {t("allDepartments")}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="relative">
        <Search className="absolute left-4 top-4 size-5 text-slate-400" />
        <Input
          aria-label={t(
            tab === "doctors" ? "searchDoctors" : "searchDepartments",
          )}
          placeholder={t(
            tab === "doctors" ? "searchDoctors" : "searchDepartments",
          )}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          className="h-14 pl-12 text-lg"
        />
      </div>
      {tab === "doctors" && (
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            variant="outline"
            className="min-h-12 h-auto whitespace-normal text-left max-w-full"
            onClick={() => changeTab("departments")}
          >
            <Building2 className="size-4" />
            {department?.name || t("filterDepartment")}
          </Button>
          {department && (
            <Button
              variant="ghost"
              className="min-h-12"
              onClick={() => {
                setDepartment(undefined);
                setOffset(0);
              }}
            >
              <X className="size-4" />
              {t("clearDepartment")}
            </Button>
          )}
        </div>
      )}
      {isFetching ? (
        <p role="status" className="py-6">
          {t("loading")}
        </p>
      ) : error ? (
        <div role="alert">
          <p>{t("error")}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500" aria-live="polite">
            {t(tab === "doctors" ? "doctorResults" : "departmentResults", {
              count: data?.count || 0,
            })}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {data?.results.map((item) => (
              <button
                key={item.id}
                disabled={busy}
                onClick={() => {
                  if (tab === "departments") {
                    setDepartment(item);
                    changeTab("doctors");
                  } else onSelect(item);
                }}
                className="flex flex-col text-left gap-2 p-4 rounded-2xl border bg-white min-h-36 hover:bg-primary-50 hover:border-primary-500 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50"
              >
                <span className="rounded-xl bg-primary-50 p-2 text-primary-700 w-fit">
                  {tab === "doctors" ? (
                    <Stethoscope className="size-5" />
                  ) : (
                    <Building2 className="size-5" />
                  )}
                </span>
                <span className="flex-1">
                  <span className="block text-lg font-semibold break-words">
                    {item.name}
                  </span>
                  <span className="block mt-1 text-sm text-slate-500">
                    {tab === "doctors"
                      ? item.departments?.join(" · ") || t("doctorVisit")
                      : t("doctorResults", { count: item.doctor_count || 0 })}
                  </span>
                </span>
                <span className="flex justify-between items-center gap-3 text-primary-700 text-sm font-medium w-full">
                  {t(tab === "doctors" ? "viewTimes" : "viewDoctors")}
                  <ArrowRight className="size-4" />
                </span>
              </button>
            ))}
          </div>
          {!data?.results.length && (
            <div className="space-y-3 py-4">
              <p className="text-slate-500">{t("noMatches")}</p>
              <Button
                variant="outline"
                className="min-h-12"
                onClick={() => {
                  setDepartment(undefined);
                  changeTab("departments");
                }}
              >
                {t("allDepartments")}
              </Button>
              <p className="text-sm text-slate-500">{t("askReception")}</p>
            </div>
          )}
        </>
      )}
      <div className="flex justify-between gap-3">
        <Button
          variant="outline"
          className="min-h-12"
          disabled={!offset || isFetching}
          onClick={() => setOffset(offset - 20)}
        >
          {t("previous")}
        </Button>
        <Button
          variant="outline"
          className="min-h-12"
          disabled={offset + 20 >= (data?.count || 0) || isFetching}
          onClick={() => setOffset(offset + 20)}
        >
          {t("more")}
        </Button>
      </div>
    </div>
  );
}
